import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Mail, Building, Calendar, DollarSign, MessageSquare, Plus } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { leadsApi } from '@/services/api/leads'
import { 
  STATUS_LABELS, 
  STATUS_COLORS, 
  PROJECT_TYPE_LABELS,
  BUDGET_RANGE_LABELS,
  TIMELINE_LABELS,
  type LeadStatus 
} from '@/types/leads'
import { format } from 'date-fns'

export default function LeadDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showAddNote, setShowAddNote] = useState(false)
  const [noteText, setNoteText] = useState('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['lead', id],
    queryFn: () => leadsApi.getLead(parseInt(id!)),
    enabled: !!id
  })

  const updateStatusMutation = useMutation({
    mutationFn: ({ status, notes }: { status: LeadStatus; notes?: string }) => 
      leadsApi.updateLead(parseInt(id!), { status, notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', id] })
      queryClient.invalidateQueries({ queryKey: ['admin-leads'] })
      setShowAddNote(false)
      setNoteText('')
    }
  })

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  if (error || !data?.data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">Error loading lead details</div>
          <Link to="/admin/leads" className="text-indigo-600 hover:text-indigo-800">
            Back to Leads
          </Link>
        </div>
      </div>
    )
  }

  const lead = data.data

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back Button */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/leads')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} />
          Back to Leads
        </button>
      </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Lead Info */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{lead.name}</h1>
                  <p className="text-gray-600 mt-1">{lead.email}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[lead.status]}`}>
                  {STATUS_LABELS[lead.status]}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-2 text-gray-600 mb-2">
                    <Building size={18} />
                    <span className="font-medium">Company</span>
                  </div>
                  <p className="text-gray-900">{lead.company || 'Not provided'}</p>
                </div>

                <div>
                  <div className="flex items-center gap-2 text-gray-600 mb-2">
                    <MessageSquare size={18} />
                    <span className="font-medium">Project Type</span>
                  </div>
                  <p className="text-gray-900">{lead.projectType ? PROJECT_TYPE_LABELS[lead.projectType] : 'Not specified'}</p>
                </div>

                <div>
                  <div className="flex items-center gap-2 text-gray-600 mb-2">
                    <DollarSign size={18} />
                    <span className="font-medium">Budget Range</span>
                  </div>
                  <p className="text-gray-900">
                    {lead.budgetRange ? BUDGET_RANGE_LABELS[lead.budgetRange] : 'Not specified'}
                  </p>
                </div>

                <div>
                  <div className="flex items-center gap-2 text-gray-600 mb-2">
                    <Calendar size={18} />
                    <span className="font-medium">Timeline</span>
                  </div>
                  <p className="text-gray-900">{TIMELINE_LABELS[lead.timeline]}</p>
                </div>
              </div>

              <div className="mt-6">
                <h3 className="font-medium text-gray-900 mb-2">Message</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700 whitespace-pre-wrap">{lead.message}</p>
                </div>
              </div>
            </div>

            {/* Interactions History */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Interaction History</h2>
                <button
                  onClick={() => setShowAddNote(true)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm transition-colors"
                >
                  <Plus size={16} />
                  Add Note
                </button>
              </div>

              {showAddNote && (
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Add a note..."
                    className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    rows={3}
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => {
                        updateStatusMutation.mutate({
                          status: lead.status,
                          notes: noteText
                        })
                      }}
                      disabled={!noteText.trim() || updateStatusMutation.isPending}
                      className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm transition-colors disabled:opacity-50"
                    >
                      Save Note
                    </button>
                    <button
                      onClick={() => {
                        setShowAddNote(false)
                        setNoteText('')
                      }}
                      className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {lead.interactions && lead.interactions.length > 0 ? (
                  lead.interactions.map((interaction) => (
                    <div key={interaction.id} className="border-l-2 border-gray-200 pl-4 pb-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm text-gray-600 mb-1">
                            {format(new Date(interaction.createdAt), 'MMM d, yyyy h:mm a')}
                          </p>
                          <p className="text-gray-900">{interaction.notes}</p>
                          {interaction.type === 'status_change' && (
                            <span className="inline-block mt-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                              Status Changed
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">No interactions yet</p>
                )}

                {/* Initial submission */}
                <div className="border-l-2 border-gray-200 pl-4">
                  <p className="text-sm text-gray-600 mb-1">
                    {lead.createdAt ? format(new Date(lead.createdAt), 'MMM d, yyyy h:mm a') : 'N/A'}
                  </p>
                  <p className="text-gray-900">Lead submitted through contact form</p>
                  <span className="inline-block mt-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                    Initial Contact
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Update Status
                  </label>
                  <select
                    value={lead.status}
                    onChange={(e) => updateStatusMutation.mutate({
                      status: e.target.value as LeadStatus
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    {Object.entries(STATUS_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                <a
                  href={`mailto:${lead.email}`}
                  className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                >
                  <Mail size={18} />
                  Send Email
                </a>
              </div>
            </div>

            {/* Metadata */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Details</h3>
              
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-600">Lead ID</dt>
                  <dd className="text-sm text-gray-900 mt-1">#{lead.id}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600">Source</dt>
                  <dd className="text-sm text-gray-900 mt-1 capitalize">{lead.source?.replace(/_/g, ' ') || 'Unknown'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600">Created</dt>
                  <dd className="text-sm text-gray-900 mt-1">
                    {lead.createdAt ? format(new Date(lead.createdAt), 'MMM d, yyyy h:mm a') : 'N/A'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600">Last Updated</dt>
                  <dd className="text-sm text-gray-900 mt-1">
                    {lead.updatedAt ? format(new Date(lead.updatedAt), 'MMM d, yyyy h:mm a') : 'N/A'}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
    </div>
  )
}